import { useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useSearchParams,
} from "react-router-dom";
import type { WebsiteVisitIntent } from "@sales-ops-amplify/shared";
import { WORKFLOW_TEMPLATES } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { VisitorsSettingsPanel } from "@/components/visitors/visitors-settings-panel";
import {
  useVisitorAnalytics,
  useVisitorCompanies,
  useVisitorCompanyDetail,
  useVisitorPeople,
  useVisitorPersonDetail,
  useSeedVisitorSamples,
} from "@/hooks/useVisitors";
import { useWorkflows } from "@/hooks/useWorkflows";

const TABS = [
  { to: "/visitors/companies", label: "Visited companies", end: false },
  { to: "/visitors/people", label: "Visited people", end: false },
  { to: "/visitors/analytics", label: "Analytics", end: false },
  { to: "/visitors/workflows", label: "Workflows", end: false },
  { to: "/visitors/settings", label: "Settings", end: false },
];

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

function intentBadge(intent: WebsiteVisitIntent) {
  const tone =
    intent === "high"
      ? "bg-emerald-500/15 text-emerald-600"
      : intent === "medium"
        ? "bg-amber-500/15 text-amber-600"
        : "bg-muted text-muted-foreground";
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium uppercase", tone)}>
      {intent}
    </span>
  );
}

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function VisitorsLayout() {
  return (
    <div className="space-y-5 pb-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Website visitors
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Identify companies and people visiting your site, score intent, and
          automate follow-up.
        </p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

/** @deprecated use nested routes */
export function VisitorsPage() {
  return <Navigate to="/visitors/companies" replace />;
}

function ListFilters({
  q,
  setQ,
  range,
  setRange,
  intent,
  setIntent,
}: {
  q: string;
  setQ: (v: string) => void;
  range: string;
  setRange: (v: string) => void;
  intent: string;
  setIntent: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search…"
        className="w-56 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <select
        value={range}
        onChange={(e) => setRange(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-2 text-sm"
      >
        <option value="7">Last 7 days</option>
        <option value="28">Last 28 days</option>
        <option value="90">Last 90 days</option>
        <option value="all">All time</option>
      </select>
      <select
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-2 text-sm"
      >
        <option value="">Any intent</option>
        <option value="low">Low+</option>
        <option value="medium">Medium+</option>
        <option value="high">High</option>
      </select>
    </div>
  );
}

export function VisitorsCompaniesPage() {
  const [q, setQ] = useState("");
  const [range, setRange] = useState("28");
  const [intent, setIntent] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const seed = useSeedVisitorSamples();
  const filters = useMemo(
    () => ({
      q: q || undefined,
      dateFrom: range === "all" ? undefined : daysAgoIso(Number(range)),
      intent: (intent || undefined) as WebsiteVisitIntent | undefined,
    }),
    [q, range, intent],
  );
  const list = useVisitorCompanies(filters);
  const detail = useVisitorCompanyDetail(selected ?? undefined);
  const companies = list.data?.companies ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ListFilters
          q={q}
          setQ={setQ}
          range={range}
          setRange={setRange}
          intent={intent}
          setIntent={setIntent}
        />
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
          disabled={seed.isPending}
          onClick={() => seed.mutate()}
        >
          Sample data
        </button>
      </div>

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading companies…</p>
      ) : companies.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No identified companies yet. Install the tracking script or generate
            sample visits.
          </p>
          <Link
            to="/visitors/settings"
            className="mt-3 inline-block text-sm text-brand hover:underline"
          >
            Open settings
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">Visits</th>
                  <th className="px-3 py-2 font-medium">Unique</th>
                  <th className="px-3 py-2 font-medium">Intent</th>
                  <th className="px-3 py-2 font-medium">Top page</th>
                  <th className="px-3 py-2 font-medium">Last visit</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr
                    key={c.companyKey}
                    className={cn(
                      "cursor-pointer border-b border-border/60 hover:bg-muted/30",
                      selected === c.companyKey && "bg-brand/5",
                    )}
                    onClick={() => setSelected(c.companyKey)}
                  >
                    <td className="px-3 py-2 font-medium text-foreground">
                      {c.name}
                      <span className="block text-xs text-muted-foreground">
                        {c.domain}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {c.visitCount}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {c.uniqueVisitors}
                    </td>
                    <td className="px-3 py-2">{intentBadge(c.maxIntent)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {c.topPages[0]?.path ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {formatWhen(c.lastVisitAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="rounded-lg border border-border bg-card p-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Select a company for visit timeline.
              </p>
            ) : detail.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : detail.data ? (
              <div className="space-y-3">
                <div>
                  <p className="font-medium">{detail.data.company.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {detail.data.company.visitCount} visits ·{" "}
                    {intentBadge(detail.data.company.maxIntent)}
                  </p>
                </div>
                {detail.data.company.accountId ? (
                  <Link
                    to={`/user/accounts/${detail.data.company.accountId}`}
                    className="text-sm text-brand hover:underline"
                  >
                    Open company
                  </Link>
                ) : null}
                <ul className="max-h-80 space-y-2 overflow-y-auto text-sm">
                  {detail.data.pageviews.map((pv) => (
                    <li key={pv.pageviewId} className="border-b border-border/50 pb-2">
                      <p className="text-foreground">{pv.path}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatWhen(pv.occurredAt)} · {pv.intent}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-destructive">Failed to load.</p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export function VisitorsPeoplePage() {
  const [q, setQ] = useState("");
  const [range, setRange] = useState("28");
  const [intent, setIntent] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const filters = useMemo(
    () => ({
      q: q || undefined,
      dateFrom: range === "all" ? undefined : daysAgoIso(Number(range)),
      intent: (intent || undefined) as WebsiteVisitIntent | undefined,
    }),
    [q, range, intent],
  );
  const list = useVisitorPeople(filters);
  const detail = useVisitorPersonDetail(selected ?? undefined);
  const people = list.data?.people ?? [];

  return (
    <div className="space-y-4">
      <ListFilters
        q={q}
        setQ={setQ}
        range={range}
        setRange={setRange}
        intent={intent}
        setIntent={setIntent}
      />
      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading people…</p>
      ) : people.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No identified people yet. Person identification uses email-click
            handoff (`zso_cid`) or collect payload leadId when mode is
            Company &amp; person.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Person</th>
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">Visits</th>
                  <th className="px-3 py-2 font-medium">Intent</th>
                  <th className="px-3 py-2 font-medium">Campaign</th>
                  <th className="px-3 py-2 font-medium">Last visit</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr
                    key={p.leadId}
                    className={cn(
                      "cursor-pointer border-b border-border/60 hover:bg-muted/30",
                      selected === p.leadId && "bg-brand/5",
                    )}
                    onClick={() => setSelected(p.leadId)}
                  >
                    <td className="px-3 py-2">
                      <span className="font-medium text-foreground">{p.name}</span>
                      {p.title ? (
                        <span className="block text-xs text-muted-foreground">
                          {p.title}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {p.accountId ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {p.visitCount}
                    </td>
                    <td className="px-3 py-2">{intentBadge(p.maxIntent)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {p.utmCampaign ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {formatWhen(p.lastVisitAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="rounded-lg border border-border bg-card p-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Select a person for visit timeline.
              </p>
            ) : detail.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : detail.data ? (
              <div className="space-y-3">
                <div>
                  <p className="font-medium">{detail.data.person.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {detail.data.person.visitCount} visits
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <Link
                    to={`/user/leads?q=${encodeURIComponent(detail.data.person.name)}`}
                    className="text-brand hover:underline"
                  >
                    Open contact
                  </Link>
                  <Link
                    to={`/workflows?template=website-visit-sequence`}
                    className="text-brand hover:underline"
                  >
                    Start workflow
                  </Link>
                </div>
                <ul className="max-h-80 space-y-2 overflow-y-auto text-sm">
                  {detail.data.pageviews.map((pv) => (
                    <li key={pv.pageviewId} className="border-b border-border/50 pb-2">
                      <p className="text-foreground">{pv.path}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatWhen(pv.occurredAt)} · ref{" "}
                        {pv.referrer
                          ? (() => {
                              try {
                                return new URL(pv.referrer).hostname;
                              } catch {
                                return pv.referrer;
                              }
                            })()
                          : "—"}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-destructive">Failed to load.</p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export function VisitorsAnalyticsPage() {
  const [search] = useSearchParams();
  const range = search.get("range") ?? "28";
  const [days, setDays] = useState(range);
  const analytics = useVisitorAnalytics({
    dateFrom: days === "all" ? undefined : daysAgoIso(Number(days)),
  });
  const data = analytics.data;
  const maxTrend = Math.max(1, ...(data?.trend.map((t) => t.pageviews) ?? [1]));
  const maxPage = Math.max(1, ...(data?.topPages.map((p) => p.count) ?? [1]));

  return (
    <div className="space-y-6">
      <select
        value={days}
        onChange={(e) => setDays(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-2 text-sm"
      >
        <option value="7">Last 7 days</option>
        <option value="28">Last 28 days</option>
        <option value="90">Last 90 days</option>
        <option value="all">All time</option>
      </select>

      {analytics.isLoading || !data ? (
        <p className="text-sm text-muted-foreground">Loading analytics…</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Pageviews", data.pageviews],
              ["Companies identified", data.identifiedCompanies],
              ["People identified", data.identifiedPeople],
              ["Engaged companies", data.engagedCompanies],
            ].map(([label, value]) => (
              <div
                key={String(label)}
                className="rounded-lg border border-border bg-card p-4"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {label}
                </p>
                <p className="mt-1 text-2xl font-bold">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Visit trend
              </h3>
              {data.trend.length === 0 ? (
                <p className="text-sm text-muted-foreground">No data yet.</p>
              ) : (
                <div className="flex items-end gap-1" style={{ height: 120 }}>
                  {data.trend.map((t) => (
                    <div
                      key={t.day}
                      className="flex flex-1 flex-col items-center gap-1"
                      title={`${t.day}: ${t.pageviews}`}
                    >
                      <div
                        className="w-full rounded-sm bg-brand"
                        style={{
                          height: `${Math.max(
                            4,
                            Math.round((t.pageviews / maxTrend) * 100),
                          )}%`,
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Top pages
              </h3>
              <div className="space-y-2">
                {data.topPages.map((p) => (
                  <div key={p.path} className="flex items-center gap-3 text-sm">
                    <span className="w-40 truncate text-foreground">{p.path}</span>
                    <div className="flex-1">
                      <div className="h-2 overflow-hidden rounded-full bg-muted/20">
                        <div
                          className="h-full rounded-full bg-brand"
                          style={{
                            width: `${Math.round((p.count / maxPage) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <span className="w-8 text-right text-muted-foreground">
                      {p.count}
                    </span>
                  </div>
                ))}
                {data.topPages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No pages yet.</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Top companies
            </h3>
            <ul className="space-y-2 text-sm">
              {data.topCompanies.map((c) => (
                <li key={c.key} className="flex justify-between">
                  <span className="text-foreground">{c.name}</span>
                  <span className="text-muted-foreground">
                    {c.visitCount} visits
                  </span>
                </li>
              ))}
              {data.topCompanies.length === 0 ? (
                <li className="text-muted-foreground">No companies yet.</li>
              ) : null}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export function VisitorsWorkflowsPage() {
  const templates = WORKFLOW_TEMPLATES.filter((t) => t.category === "Inbound");
  const workflows = useWorkflows({ status: "all" });
  const visitorWfs = (workflows.data?.workflows ?? []).filter(
    (w) => w.trigger.type === "website_visited",
  );

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Preset plays
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Link
              key={t.id}
              to={`/workflows?template=${encodeURIComponent(t.id)}`}
              className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
            >
              <p className="font-medium text-foreground">{t.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t.description}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Website-visited workflows
          </h2>
          <Link
            to="/workflows"
            className="text-sm text-brand hover:underline"
          >
            Open Workflows
          </Link>
        </div>
        {workflows.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : visitorWfs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active website_visited workflows yet. Create one from a preset.
          </p>
        ) : (
          <ul className="space-y-2">
            {visitorWfs.map((w) => (
              <li key={w.workflowId}>
                <Link
                  to={`/workflows/${w.workflowId}`}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-sm hover:border-brand/40"
                >
                  <span className="font-medium text-foreground">{w.name}</span>
                  <span className="text-muted-foreground">{w.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function VisitorsSettingsPage() {
  return <VisitorsSettingsPanel />;
}
